import { Button, Modal, Spinner } from '@ag1/ui';
import Select from '@portal/shared/ui/Select';
import { SSFlags } from '@portal/state/context/StatsigContext/StatsigFlags';
import { useGateValue } from '@statsig/react-bindings';
import { useMutation } from '@tanstack/react-query';
import { currencyChangeEvent, languageChangeEvent } from 'analytics/events/studio';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { JPShortLocale, LanguagesOptions, ShortLocaleEnum, fullLocales } from 'schema/locale';
import { AU_NZ_STORES, StoreCode, StoreEnum } from 'schema/storeCode';
import { PageTranslation } from 'schema/studio/studio';
import { useLocale } from 'shared/hooks/useLocale';
import {
  getFullLocale,
  getShortLocale,
  getStoreOptions,
  getTranslationSlug
} from 'shared/utils/locale';
import { safeLocalStorage } from 'shared/utils/safeLocalStorage';
import { cartAtom, currentCartIdAtom } from 'state/cart/atoms';
import { setAgLang } from 'state/cookies/agLang';
import { setAgStore } from 'state/cookies/agStore';
import { RegionSelector } from 'types/studio/header';
import { useStoreValue } from '../../../state/hooks/useStore';
import { useSiteCart } from '../SiteCart/hooks/SiteCartContext';
import styles from './RegionSelectorModal.module.scss';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  pageTranslations?: PageTranslation[];
  text?: RegionSelector | null;
}

const JAPANESE_STORE = 'https://jp.drinkag1.com/';

const RegionSelectorModal = ({ isOpen, onClose, pageTranslations = [], text }: Props) => {
  const [selectedLocale, setSelectedLocale] = useState<ShortLocaleEnum>();
  const [selectedStore, setSelectedStore] = useState<StoreCode>();
  const [, setCartV2] = useAtom(cartAtom);
  const [, setCartId] = useAtom(currentCartIdAtom);

  const { deleteCart } = useSiteCart();

  const isAuNzEnabled = useGateValue(SSFlags.enableAuNz);

  const router = useRouter();
  const locale = useLocale();
  const store = useStoreValue();

  const handleCloseModal = () => {
    onClose();
  };

  const fullSelectedLocale = useMemo(
    () => getFullLocale(selectedLocale, selectedStore),
    [selectedLocale, selectedStore]
  );

  useEffect(() => {
    setSelectedLocale(getShortLocale(locale));
    setSelectedStore(store);
  }, [locale, store, isOpen]);

  const handleChangeLanguage = (value: ShortLocaleEnum) => {
    setSelectedLocale(value);

    if (value === ShortLocaleEnum.de) {
      setSelectedStore(StoreEnum.eu);
    } else if (value === ShortLocaleEnum.nl) {
      setSelectedStore(StoreEnum.eu);
    } else if (value === ShortLocaleEnum.se) {
      setSelectedStore(StoreEnum.eu);
    } else if (value === ShortLocaleEnum.es) {
      setSelectedStore(StoreEnum.eu);
    } else if (value === ShortLocaleEnum.fr) {
      setSelectedStore(StoreEnum.eu);
    } else if (value === JPShortLocale) {
      setSelectedStore(StoreEnum.jp);
    } else if (value === ShortLocaleEnum.apac) {
      setSelectedStore(StoreEnum.us);
    }
  };

  const availableStores = useMemo(() => {
    const options = getStoreOptions();

    const possibleStores = fullLocales
      .filter(({ language }) => language === selectedLocale)
      .map((item) => item.store);

    const uniqueStores = possibleStores.filter(
      (item, index) => possibleStores.indexOf(item) === index
    );

    const allowedStores = isAuNzEnabled
      ? uniqueStores
      : uniqueStores.filter((item) => !AU_NZ_STORES.includes(item as StoreEnum));

    return options.filter((item) => allowedStores.includes(item.value));
  }, [selectedLocale, isAuNzEnabled]);

  const isOrderDetails =
    router.asPath.includes('/subscriptions/') || router.asPath.includes('/orders/');

  const newLink = useMemo(() => {
    const currentLocale = getFullLocale(selectedLocale, selectedStore);
    const currentTranslation = pageTranslations.find((item) => item?.language === currentLocale);

    return {
      ...currentTranslation,
      slug: {
        current: getTranslationSlug({
          path: isOrderDetails ? router.asPath.split('/').slice(0, -1).join('/') : router.asPath,
          slug: currentTranslation?.slug.current,
          store: selectedStore as StoreEnum,
          locale: selectedLocale
        })
      }
    };
  }, [router.asPath, selectedLocale, selectedStore, pageTranslations, isOrderDetails]);

  const { mutate: handleApply, isPending: isApplyLoading } = useMutation({
    mutationFn: async () => {
      const shortLocale = getShortLocale(fullSelectedLocale);
      if (selectedStore === StoreEnum.jp) {
        safeLocalStorage.removeItem('cartId');
        deleteCart();
        setCartV2(null);
        setCartId(null);
        setAgStore(StoreEnum.us);
        setAgLang(ShortLocaleEnum.en);
        languageChangeEvent(ShortLocaleEnum.en);

        await router.push(JAPANESE_STORE, undefined, { locale: false });
        handleCloseModal();
        return;
      }

      if (selectedStore && newLink) {
        setAgStore(selectedStore);
        currencyChangeEvent(selectedStore);
        setAgLang(shortLocale);
        languageChangeEvent(shortLocale);
        safeLocalStorage.removeItem('cartId');
        deleteCart();
        setCartV2(null);
        setCartId(null);

        await router.push(newLink.slug.current, undefined, { locale: false });
      }

      handleCloseModal();
    }
  });

  const handleChangeStore = (value: StoreEnum) => {
    setSelectedStore(value);

    if (value === StoreEnum.jp) {
      setSelectedLocale(JPShortLocale);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal onClose={handleCloseModal} isOpen={isOpen} mode="center">
      <Modal.Header>{text?.title}</Modal.Header>
      <Modal.Content hasFooter>
        <div className={styles.content}>
          <span className={styles.description}>{text?.subtitle}</span>
          <Select
            placeholder={text?.language || 'Language'}
            label={text?.language || 'Language'}
            value={selectedLocale}
            options={LanguagesOptions}
            onChange={handleChangeLanguage}
            isSearchable={false}
            isClearable={false}
            name="language"
          />
          <Select
            placeholder={text?.currency || 'Currency'}
            label={text?.currency || 'Currency'}
            value={selectedStore}
            options={availableStores}
            onChange={handleChangeStore}
            isSearchable={false}
            isClearable={false}
            name="currency"
          />
        </div>
      </Modal.Content>
      <Modal.Footer>
        <div className={styles.footer}>
          <Button
            variant="outlineGrey"
            size="small"
            type="submit"
            onClick={handleCloseModal}
            className={styles.button}>
            {text?.cancel || 'Cancel'}
          </Button>
          <Button
            variant="secondary"
            size="small"
            type="submit"
            onClick={() => handleApply()}
            disabled={isApplyLoading}
            className={clsx(styles.button, styles.save)}>
            {isApplyLoading ? (
              <Spinner color="brand-white" size={24} withOverlay={false} />
            ) : (
              text?.save || 'Save'
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default RegionSelectorModal;
